import { useState, useEffect } from 'react'
import { useCurrentUser } from '~/common/use-current-user'
import { supabaseGetter } from '~/api/supabase-getter'

export const useAccountCropCount = () => {
  const { currentUser } = useCurrentUser()
  const [cropCount, setCropCount] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchCount() {
      if (!currentUser?.id) {
        setCropCount(null)
        setLoading(false)
        setError(null)
        return
      }
      setLoading(true)
      setError(null)
      try {
        const { data: membershipData, error: membershipError } = await supabaseGetter.organizationUsers.getByUser(currentUser.id)
        if (membershipError) throw membershipError

        const directOrgIds = (membershipData || []).map(o => o.organization_id)
        if (!directOrgIds.length) { setCropCount(0); return }

        // Resolve org types so we can traverse the hierarchy downward
        const { data: orgs, error: orgsError } = await supabaseGetter.organizations.getByIds(directOrgIds)
        if (orgsError) throw orgsError

        const farmIds = (orgs || []).filter(o => o.organization_type === 'farm').map(o => o.id)
        const directPlotIds = (orgs || []).filter(o => o.organization_type === 'plot').map(o => o.id)

        // Expand farms → plots
        const farmPlotResults = await Promise.all(farmIds.map(id => supabaseGetter.organizations.getPlots(id)))
        const farmPlotIds = farmPlotResults.flatMap(r => (r.data || []).map(p => p.id))

        const allPlotIds = [...new Set([...directPlotIds, ...farmPlotIds])]

        // Expand plots → patches
        const plotPatchResults = await Promise.all(allPlotIds.map(id => supabaseGetter.organizations.getPatches(id)))
        const allPatchIds = plotPatchResults.flatMap(r => (r.data || []).map(p => p.id))

        const allOrgIds = [...new Set([...directOrgIds, ...farmPlotIds, ...allPatchIds])]

        const { data: count, error: countError } = await supabaseGetter.crops.countUniqueByOrganizations(allOrgIds)
        if (countError) throw countError
        setCropCount(count)
      } catch (err) {
        console.error('Error fetching crop count:', err)
        setError(err.message)
        setCropCount(null)
      } finally {
        setLoading(false)
      }
    }
    fetchCount()
  }, [currentUser?.id])

  return { cropCount, loading, error }
}
